import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Button } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../hooks/useAuth';
import { followUser, unfollowUser, checkIfFollowing } from '../../services/userService';

const UserListItem = ({ userId, showFollowButton = true, onFollowChange }) => {
  const { userData } = useUserData(userId);
  const { currentUser } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [followsYou, setFollowsYou] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Verificar estados de seguimiento
  useEffect(() => {
    const checkStates = async () => {
      if (currentUser && userId && currentUser.uid !== userId) {
        // Verificar si nosotros seguimos a este usuario
        const following = await checkIfFollowing(currentUser.uid, userId);
        setIsFollowing(following);
        
        // Verificar si este usuario nos sigue
        const followsBack = await checkIfFollowing(userId, currentUser.uid);
        setFollowsYou(followsBack);
      }
    };
    
    checkStates();
  }, [currentUser, userId]);
  
  // Handler para seguir/dejar de seguir
  const handleFollowToggle = async () => {
    if (loading || !currentUser) return;
    
    setLoading(true);
    
    try {
      if (isFollowing) {
        const result = await unfollowUser(userId);
        if (result.success) {
          setIsFollowing(false);
          if (onFollowChange) onFollowChange();
        } else {
          alert(result.error || 'Error al dejar de seguir');
        }
      } else {
        const result = await followUser(userId);
        if (result.success) {
          setIsFollowing(true);
          if (onFollowChange) onFollowChange();
        } else {
          alert(result.error || 'Error al seguir');
        }
      }
    } catch (error) {
      console.error('Error en handleFollowToggle:', error);
      alert('Ocurrió un error. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  
  if (!userData) {
    return (
      <div className="flex items-center gap-3 p-4 animate-pulse">
        <div className="w-12 h-12 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-32 mb-2"></div>
          <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-24"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors border-b border-gray-100 dark:border-slate-700 last:border-b-0">
      <Link 
        to={`/profile/${userId}`} 
        className="flex items-center gap-3 flex-1 min-w-0"
        onClick={(e) => {
          // Permitir que el link funcione normalmente
        }}
      >
        <Avatar
          src={userData.photoURL}
          size="lg"
          fallbackText={userData.displayName}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <PremiumName
              name={userData.displayName || 'Usuario'}
              isPremium={userData.isPremium}
              premiumColor={userData.premiumNameColor}
              nameClassName="font-medium text-gray-900 dark:text-white truncate"
            />
            {(userData.isAmbassador || userData.verified) && (
              <svg className={`w-4 h-4 flex-shrink-0 ${userData.isAmbassador ? 'text-violet-500' : 'text-blue-500'}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {followsYou && (
              <span className="text-xs bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full flex-shrink-0">
                Te sigue
              </span>
            )}
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400 truncate block">
            @{userData.username || userData.email?.split('@')[0] || 'usuario'}
          </span>
        </div>
      </Link>
      
      {showFollowButton && currentUser?.uid !== userId && (
        <Button
          variant={isFollowing ? "outline" : "gradient"}
          size="sm"
          onClick={handleFollowToggle}
          loading={loading}
          disabled={loading}
          className="ml-3 flex-shrink-0"
        >
          {isFollowing ? 'Siguiendo' : 'Seguir'}
        </Button>
      )}
      
      {currentUser?.uid === userId && (
        <span className="text-sm text-gray-500 dark:text-gray-400 ml-3 flex-shrink-0">
          Tú
        </span>
      )}
    </div>
  );
};

export default UserListItem;
